/**
 * calcularProximaFatura.js
 *
 * Pure JavaScript function that replicates the billing cycle calculation from
 * marcos-backend/cron/emitir-fatura.js for use in the modal's real-time preview.
 *
 * This function is intentionally timezone-naive (day-level only), matching the cron behavior.
 */

import {
  isExists,
  subDays,
  lastDayOfMonth,
  addDays,
  differenceInCalendarMonths,
} from 'date-fns';

/**
 * Helper function to parse and normalize date values.
 * Mirrors the getDateValue() helper from the backend cron.
 *
 * @param {Date|string|null} value - A date value (Date object, ISO string, or null)
 * @returns {Date|null} - A normalized Date object (day-level, no time), or null
 */
function getDateValue(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value === 'string') {
    const [datePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);

    if (year && month && day) {
      return new Date(year, month - 1, day);
    }
  }

  return null;
}

/**
 * Calculate the next invoice emission and due date for a recurring billing.
 *
 * Mirrors the cycle calculation logic from emitir-fatura.js, but returns
 * the UPCOMING emission (whether in this month or next month) for real-time preview.
 *
 * @param {Object} params
 * @param {number} params.diaVencimento - Due day of month (1–31)
 * @param {number} params.diasAntes - Days before emission (send date calculation)
 * @param {string} params.periodo - Billing period ('mensal'|'trimestral'|'semestral'|'anual')
 * @param {Object} params.ultimaFatura - Last invoice object with { vencimento, fake_vencimento }
 * @param {number|null} params.valor - Custom value or null (null → use produto.valor)
 * @param {Object} [params.produto] - Product object with { valor } field
 * @param {Date} [params.now] - Reference date; defaults to today if omitted
 *
 * @returns {Object|null} - { dataEmissao, dataVencimento, valor } or null if calculation fails
 */
function calcularProximaFatura({
  diaVencimento,
  diasAntes,
  periodo,
  ultimaFatura,
  valor,
  produto,
  now,
}) {
  // Validate inputs
  if (!diaVencimento || !diasAntes || !periodo || !ultimaFatura) {
    return null;
  }

  // Use current date if not provided
  const referenceDate = now instanceof Date ? now : new Date();
  const dateNow = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate()
  );

  const mesAtual = dateNow.getMonth();
  const anoAtual = dateNow.getFullYear();

  let check;
  let proximoVencimento;

  // Mirror the cron's day-level calculation
  if (diasAntes >= diaVencimento) {
    // Invoice is due next month
    const proxMes = mesAtual === 11 ? 0 : mesAtual + 1;
    const proxAno = mesAtual === 11 ? anoAtual + 1 : anoAtual;

    const ehData = isExists(proxAno, proxMes, diaVencimento);

    if (ehData) {
      // The day exists in the next month
      proximoVencimento = new Date(proxAno, proxMes, diaVencimento);
      check = subDays(proximoVencimento, diasAntes);
    } else {
      // The day doesn't exist (e.g., Feb 31), use last day of next month then first day of month after
      const primeiroDiaProximoMes = new Date(proxAno, proxMes, 1);
      proximoVencimento = primeiroDiaProximoMes;
      check = subDays(lastDayOfMonth(primeiroDiaProximoMes), diasAntes);
    }
  } else {
    // Invoice is due this month
    const ehData = isExists(anoAtual, mesAtual, diaVencimento);

    if (ehData) {
      // The day exists in the current month
      proximoVencimento = new Date(anoAtual, mesAtual, diaVencimento);
      check = subDays(proximoVencimento, diasAntes);
    } else {
      // The day doesn't exist in the current month, use last day of current month
      // and first day of next month
      proximoVencimento = addDays(lastDayOfMonth(dateNow), 1);
      check = subDays(lastDayOfMonth(dateNow), diasAntes);
    }
  }

  // Verify the billing period constraint
  const dataReferenciaPeriodo = getDateValue(ultimaFatura.vencimento)
    || getDateValue(ultimaFatura.fake_vencimento);

  if (!dataReferenciaPeriodo) {
    return null;
  }

  const diferencaMes = differenceInCalendarMonths(
    proximoVencimento,
    dataReferenciaPeriodo
  );

  // Period minimum months mapping
  const periodoMinMeses = {
    mensal: 1,
    trimestral: 3,
    semestral: 6,
    anual: 12,
  };
  const minMeses = periodoMinMeses[periodo] || 1;

  // If not enough months have passed, we need to advance to the next valid cycle
  // For preview purposes, keep advancing until we reach the minimum period
  let currentVencimento = proximoVencimento;
  let currentCheck = check;
  let currentDiferencaMes = diferencaMes;

  while (currentDiferencaMes < minMeses) {
    // Advance by one cycle
    const m = currentVencimento.getMonth();
    const a = currentVencimento.getFullYear();

    const nextMes = m === 11 ? 0 : m + 1;
    const nextAno = m === 11 ? a + 1 : a;

    const ehData = isExists(nextAno, nextMes, diaVencimento);

    if (ehData) {
      currentVencimento = new Date(nextAno, nextMes, diaVencimento);
    } else {
      currentVencimento = new Date(nextAno, nextMes, 1);
    }

    currentCheck = subDays(currentVencimento, diasAntes);
    currentDiferencaMes = differenceInCalendarMonths(
      currentVencimento,
      dataReferenciaPeriodo
    );
  }

  // Determine final value
  const produtoValor = produto ? produto.valor : null;
  const valorFinal = (valor !== null && valor !== undefined) ? valor : produtoValor;

  return {
    dataEmissao: currentCheck,
    dataVencimento: currentVencimento,
    valor: valorFinal,
  };
}

export default calcularProximaFatura;
